// API base URL: /api on the host that served this page. The web container's
// nginx proxies it to the API, in development (docker compose) and on the
// servers alike. Override at runtime to point a page at another backend:
// `localStorage.setItem('divecohorts_api_base', 'https://your-api.example.com/api')`.
export const API_BASE =
  localStorage.getItem("divecohorts_api_base") || window.location.origin + "/api";

import { stripMetadata } from "./exifStrip.js";

const TOKEN_KEY = "divecohorts_token";

export class ApiError extends Error {
  /** `code` is the API's machine-readable reason, when it sends one
   * (currently just "email_not_verified"). Kept alongside the human
   * message so callers can branch on the cause without string-matching. */
  constructor(status, message, code = null, fields = {}) {
    super(message);
    this.status = status;
    this.code = code;
    // {field: message} when the API's message is about one input, for a
    // form that shows it beside that input.
    this.fields = fields;
  }
}

/** Fired on the window whenever any request is refused because the
 * account's email isn't confirmed. Dispatched as a DOM event rather than
 * calling into auth.js/app.js directly, because those already import
 * this module — an event keeps the dependency one-way. */
export const EMAIL_UNVERIFIED_EVENT = "divecohorts:email-unverified";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function buildUrl(path, query) {
  const url = new URL(API_BASE + path);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) url.searchParams.set(key, value);
    });
  }
  return url;
}

async function request(method, path, { body, query } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    throw new ApiError(0, "Could not reach the server. Is the backend running?");
  }

  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      // Defense in depth: the backend should always return JSON (see the
      // Flask HTTPException handler in app/__init__.py), but if some
      // future error path ever returns HTML/plain text instead, fail
      // into a clean ApiError below rather than an uncaught SyntaxError.
      data = null;
    }
  }

  if (!response.ok) {
    const message = data?.error || `Request failed (${response.status})`;
    const code = data?.code || null;
    // Every gated endpoint fails the same way, so explain it once here
    // instead of teaching each of the ~20 call sites what a 403 with
    // this code means.
    if (response.status === 403 && code === "email_not_verified") {
      window.dispatchEvent(
        new CustomEvent(EMAIL_UNVERIFIED_EVENT, { detail: { message } })
      );
    }
    throw new ApiError(response.status, message, code, data?.fields || {});
  }
  return data;
}

/** How many items a feed asks for at a time. Mirrors DEFAULT_LIMIT in
 *  app/pagination.py; the server caps anything larger. */
export const PAGE_SIZE = 20;

/**
 * One page of a paginated feed.
 *
 * Sending `limit` is what opts into the envelope — without it the API
 * returns the bare array older clients expect. A response that is still
 * an array is normalised here, so a caller never has to care which shape
 * came back.
 */
export async function getPage(path, { query = {}, cursor = null, limit = PAGE_SIZE } = {}) {
  const params = { ...query, limit };
  if (cursor) params.cursor = cursor;
  const body = await request("GET", path, { query: params });
  if (Array.isArray(body)) return { items: body, nextCursor: null, total: body.length };
  return {
    items: body?.items ?? [],
    nextCursor: body?.next_cursor ?? null,
    // Only endpoints that opt into with_total report one — the galleries
    // and /dive-plans. Elsewhere the count is unknown, and null says so
    // rather than claiming the page length is the total.
    total: body?.total ?? null,
  };
}

/**
 * Saves a file the API serves (a Subsurface export) under the name the API
 * gives it. Fetched rather than linked to, so a refusal ("more than 1,000
 * dive sites") is shown as a message instead of opening a page of JSON.
 * Resolves to the filename.
 */
export async function downloadFile(path, query) {
  let response;
  try {
    response = await fetch(buildUrl(path, query));
  } catch (networkError) {
    throw new ApiError(0, "Could not reach the server. Is the backend running?");
  }
  if (!response.ok) {
    let data = null;
    try {
      data = JSON.parse(await response.text());
    } catch (parseError) {
      data = null;
    }
    throw new ApiError(response.status, data?.error || `Request failed (${response.status})`, data?.code || null);
  }

  const disposition = response.headers.get("Content-Disposition") || "";
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] || "dive-sites.xml";
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Not revoked at once: some browsers are still reading it after click().
  setTimeout(() => URL.revokeObjectURL(url), 10_000)?.unref?.();
  return filename;
}

export const api = {
  get: (path, query) => request("GET", path, { query }),
  getPage,
  post: (path, body) => request("POST", path, { body }),
  patch: (path, body) => request("PATCH", path, { body }),
  put: (path, body) => request("PUT", path, { body }),
  delete: (path, body) => request("DELETE", path, { body }),
};

/** Content types the backend's presign endpoint will issue a credential
 * for (app/uploads/routes.py _ALLOWED_CONTENT_TYPES). Checked here too so
 * an unsupported file fails instantly with a clear message instead of
 * after a round trip. */
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/**
 * Uploads one image straight to object storage and resolves to its
 * `object_key` — the value to hand back to the API as a `photo_keys`
 * entry. The bytes never pass through the Flask server.
 *
 * Deliberately uses bare fetch() rather than request(): the second leg
 * goes to S3, not to our API, so it must carry neither our JWT (which
 * would leak the token to the storage host) nor a JSON content type
 * (the browser has to set its own multipart boundary).
 */
export async function uploadPhoto(file, { purpose } = {}) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new ApiError(400, `${file.name}: only JPEG, PNG, WebP and GIF images can be uploaded.`);
  }

  // Strip GPS coordinates and camera identifiers before anything leaves
  // the device — not after upload, because the original bytes would
  // already be sitting in a world-readable bucket by then. Falls back to
  // the original file if the format isn't handled.
  const upload = await stripMetadata(file);

  const presign = await api.post("/uploads/presign", {
    content_type: upload.type,
    ...(purpose ? { purpose } : {}),
  });
  return sendToStorage(presign, upload, file);
}

/** Files a chat message may carry besides photos, by extension: documents and
 *  dive data (CHAT_FILE_TYPES in backend/app/services/uploads.py). */
export const CHAT_FILE_EXTENSIONS = [
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "gpx", "uddf", "ssrf", "fit",
];

export function fileExtension(name) {
  const dot = (name || "").lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/**
 * Uploads a photo or file to attach to a chat message, into the chat's own
 * private bucket, and resolves to `{ key, name }` for the message's
 * `attachments`. Photos lose their location and camera details first, as on
 * posts; files go as they are.
 */
export async function uploadChatAttachment(file) {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return { key: await uploadPhoto(file, { purpose: "chat" }), name: file.name };
  }
  if (!CHAT_FILE_EXTENSIONS.includes(fileExtension(file.name))) {
    throw new ApiError(400, `${file.name}: you can attach photos, PDFs, Word, Excel and PowerPoint documents, ` +
      "text and CSV files, and GPX, UDDF, Subsurface and FIT dive files.");
  }
  const presign = await api.post("/uploads/presign", {
    purpose: "chat",
    content_type: file.type || "application/octet-stream",
    file_name: file.name,
  });
  return { key: await sendToStorage(presign, file, file), name: file.name };
}

/** The second leg of an upload: the bytes, straight to storage with the
 *  presigned credential. Resolves to the object's key. */
async function sendToStorage(presign, upload, file) {
  if (presign.max_bytes && upload.size > presign.max_bytes) {
    const limitMb = Math.round(presign.max_bytes / (1024 * 1024));
    throw new ApiError(400, `${file.name} is larger than the ${limitMb} MB limit.`);
  }

  const form = new FormData();
  // Every presigned field first, then the file last — S3 ignores any
  // form field that appears after the file part.
  Object.entries(presign.fields).forEach(([name, value]) => form.append(name, value));
  form.append("file", upload);

  let response;
  try {
    response = await fetch(presign.upload_url, { method: "POST", body: form });
  } catch (networkError) {
    // fetch() rejects with an opaque TypeError for BOTH a real network
    // failure and a CORS rejection — the browser deliberately withholds
    // which, so this can't be narrowed programmatically. Worth being
    // precise about, because the two have opposite fixes and the storage
    // host is usually reachable: a multipart POST is a CORS-safelisted
    // "simple request", so it is sent without a preflight and the bucket
    // may well have stored the object before the browser discarded the
    // reply for want of an Access-Control-Allow-Origin header. Saying
    // "could not reach" sends you chasing an outage that isn't there.
    if (navigator.onLine === false) {
      throw new ApiError(0, `You appear to be offline — ${file.name} was not uploaded.`);
    }
    throw new ApiError(
      0,
      `${file.name} could not be uploaded: storage refused the request. ` +
        `This is usually a bucket CORS policy that doesn't allow ${window.location.origin}. ` +
        `(A network outage looks identical to the browser.)`
    );
  }
  if (!response.ok) {
    // A response did arrive, so this is storage rejecting the upload
    // rather than anything CORS-related. 403 on a presigned POST almost
    // always means the request violated the signed policy — expired
    // credential, or size/content-type/acl not matching what was signed.
    const hint =
      response.status === 403
        ? " The upload credential was rejected — it may have expired, or the file may not match what was signed."
        : "";
    throw new ApiError(response.status, `Upload of ${file.name} failed (${response.status}).${hint}`);
  }

  return presign.object_key;
}
